/**
 * Google Identity Platform (GCIP) Auth Client.
 *
 * Implements the AuthClient interface on top of `firebase/auth` for GCIP
 * multi-tenant email/password sign-in with enforced TOTP MFA. The app hosts
 * the full login / MFA enrollment / MFA challenge flow (there is no upstream
 * IdP redirect anymore). DB authz (user / user_tenant_role / RLS) is
 * unchanged; the backend verifies the Firebase ID token (shared/auth.py).
 *
 * CJIS posture (PE-21): TOTP is mandatory but not phishing-resistant
 * (GCIP has no WebAuthn/passkey support). The absolute session cap (IA-11)
 * is enforced against the token's native `auth_time` claim, and MFA proof is
 * the `firebase.sign_in_second_factor` claim — both surfaced here and
 * re-verified server-side.
 */

'use client';

import type { Auth, MultiFactorResolver, MultiFactorSession, User } from 'firebase/auth';
import type { AuthClient, AuthUser } from './auth-client';
import { getFirebaseAuth } from './firebase';
import { logger } from './logger';
import { setLoggingOut } from './auth-reset';

// CJIS IA-11 / 5.6.3.1: absolute session cap (12 hours) enforced against the
// Firebase ID token auth_time claim. Silent refreshes do not reset this clock.
// Kept in sync with app/api/auth/session/route.ts and shared/auth.py.
export const SESSION_MAX_AGE_SECONDS = 12 * 60 * 60;

// Typed sign-in error codes surfaced to the login UI so it can branch to the
// TOTP enrollment or challenge step without inspecting raw Firebase errors.
export const MFA_ENROLLMENT_REQUIRED = 'MFA_ENROLLMENT_REQUIRED';
export const MFA_CHALLENGE_REQUIRED = 'MFA_CHALLENGE_REQUIRED';

// "Lost transient sign-in state" codes. These fire when the in-memory MFA
// resolver / enrollment secret is gone (a full page load wiped the singleton,
// or the resolver was already consumed by a successful resolveSignIn whose
// follow-up finishAuthenticated then failed). The login UI treats these as a
// dead-end signal to restart sign-in rather than re-submitting a code against
// a null resolver (gh #1007: "No multi-factor challenge in progress").
export const MFA_NO_CHALLENGE = 'NO_CHALLENGE';
export const MFA_NO_ENROLLMENT = 'NO_ENROLLMENT';
export const MFA_NO_USER = 'NO_USER';

// gh #877 / gh #1007: cross-tab idle-lock signalling. lockSession() posts on
// this BroadcastChannel AND stamps localStorage, because a background tab that
// was frozen/discarded by the browser never receives the BroadcastChannel
// message. The localStorage stamp survives freezing and is re-read on
// visibilitychange, so a refocused sibling tab deterministically raises the
// lock overlay instead of lingering in a half-authenticated ("?") state.
export const SESSION_LOCK_BROADCAST = 'session-lock';
export const SESSION_LOCK_STORAGE_KEY = 'foltrigg.session-lock-ts';

export class GcipAuthError extends Error {
  code: string;
  constructor(code: string, message?: string) {
    super(message || code);
    this.code = code;
  }
}

interface JwtPayload {
  auth_time?: number;
  iat?: number;
  firebase?: { sign_in_second_factor?: string; tenant?: string };
}

function decodeJwt(idToken: string | null | undefined): JwtPayload | null {
  if (!idToken) return null;
  const parts = idToken.split('.');
  if (parts.length !== 3) return null;
  try {
    const b64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    const pad = b64.length % 4 === 0 ? '' : '='.repeat(4 - (b64.length % 4));
    const json =
      typeof atob === 'function'
        ? atob(b64 + pad)
        : Buffer.from(b64 + pad, 'base64').toString('utf-8');
    return JSON.parse(json) as JwtPayload;
  } catch {
    return null;
  }
}

/** auth_time (seconds since epoch) from a Firebase ID token, or null. */
export function decodeAuthTime(idToken: string | null | undefined): number | null {
  const payload = decodeJwt(idToken);
  if (!payload) return null;
  const raw = payload.auth_time ?? payload.iat;
  return typeof raw === 'number' ? Math.floor(raw) : null;
}

/** True when the token's authenticated session has exceeded the absolute cap. */
export function isSessionCapExceeded(idToken: string | null | undefined): boolean {
  const authTime = decodeAuthTime(idToken);
  if (authTime == null) return false;
  const nowSec = Math.floor(Date.now() / 1000);
  return nowSec - authTime >= SESSION_MAX_AGE_SECONDS;
}

/** True when the token carries proof of a completed second factor. */
export function tokenHasSecondFactor(idToken: string | null | undefined): boolean {
  const payload = decodeJwt(idToken);
  return Boolean(payload?.firebase?.sign_in_second_factor);
}

function toAuthUser(user: User): AuthUser {
  return {
    uid: user.uid,
    email: user.email,
    displayName: user.displayName,
    emailVerified: user.emailVerified,
  };
}

export class GCIPAuthClient implements AuthClient {
  readonly providerName = 'gcip';
  readonly supportsPasswordManagement = true;

  // MFA is mandatory under PE-21: a single-factor session is not app-authenticated
  // until the token carries sign_in_second_factor. When enforced, signIn forces
  // TOTP enrollment and onIdTokenChanged rejects single-factor sessions.
  //
  // Driven per-env from NEXT_PUBLIC_REQUIRE_MFA (baked at build time from the
  // backend require_mfa tfvar) so the client gate stays in LOCKSTEP with the
  // backend REQUIRE_MFA env and the tenant mfaConfig.state. A mismatch is the
  // PE-23 footgun in both directions: client off + backend on → single-factor
  // tokens 401 at /api/auth/me (profile-401 loop, enrollment never runs); client
  // on + tenant DISABLED → enrollment is rejected by GCIP. Defaults to enforced
  // (only the literal "false" disables), matching the require_mfa tfvar default.
  private readonly _requireMfa = process.env.NEXT_PUBLIC_REQUIRE_MFA !== 'false';

  private _auth: Auth | null = null;
  private _listeners: Set<(user: AuthUser | null) => void> = new Set();
  private _currentUser: AuthUser | null = null;
  private _initialized = false;

  // gh #1073: true once Firebase has settled its persisted auth state AND the
  // first authoritative value has been broadcast via _notify. Before this,
  // _currentUser is a not-yet-restored null and MUST NOT be emitted, or a cold
  // document load of a protected route publishes { loading:false, user:null }
  // and pins the dashboard on its "Redirecting to login..." dead-end for
  // SELF_HEAL_RELOAD_MS. Gates the synchronous emission in onAuthStateChanged.
  // Flipped inside _notify (not at the top of the onIdTokenChanged handler),
  // so it can never be true while _currentUser is still the pre-restore null.
  private _bootstrapped = false;

  // Transient sign-in state carried across the multi-step login flow.
  private _enrollSecret: import('firebase/auth').TotpSecret | null = null;
  private _mfaResolver: MultiFactorResolver | null = null;

  private auth(): Auth {
    if (!this._auth) {
      this._auth = getFirebaseAuth();
    }
    return this._auth;
  }

  private _notify(user: AuthUser | null) {
    // gh #1073: reaching any _notify means _currentUser has been resolved to its
    // authoritative (post-restore) value, so it is now safe to emit synchronously
    // to late onAuthStateChanged subscribers. Flip the settle flag HERE — never
    // synchronously at the top of the onIdTokenChanged handler, whose fbUser
    // branch only assigns _currentUser after an `await getIdToken()`.
    this._bootstrapped = true;
    for (const listener of this._listeners) {
      try {
        listener(user);
      } catch (e) {
        logger.error('[GCIPAuthClient] Listener error', e);
      }
    }
  }

  private _ensureInitialized() {
    if (this._initialized || typeof window === 'undefined') return;
    this._initialized = true;
    // onIdTokenChanged fires on sign-in, sign-out, and token refresh. We gate
    // app-authenticated state on a completed second factor so a single-factor
    // session (mid-enrollment) is treated as unauthenticated by the app.
    import('firebase/auth').then(({ onIdTokenChanged }) => {
      onIdTokenChanged(this.auth(), async (fbUser) => {
        // Do NOT set _bootstrapped here (gh #1073 review): the fbUser-present
        // path yields at `await getIdToken()` below, and a subscriber attaching
        // during that yield would see _bootstrapped=true with _currentUser still
        // the pre-restore null — the exact spurious null this fix removes. Each
        // branch instead resolves _currentUser and then calls _notify, which
        // flips the flag only once the value is authoritative.
        if (!fbUser) {
          this._currentUser = null;
          this._notify(null);
          return;
        }
        try {
          const token = await fbUser.getIdToken();
          if (this._requireMfa && !tokenHasSecondFactor(token)) {
            this._currentUser = null;
            this._notify(null);
            return;
          }
          if (isSessionCapExceeded(token)) {
            this._currentUser = null;
            this._notify(null);
            return;
          }
          this._currentUser = toAuthUser(fbUser);
          this._notify(this._currentUser);
        } catch (e) {
          logger.warn('[GCIPAuthClient] onIdTokenChanged token read failed', e);
          this._currentUser = null;
          this._notify(null);
        }
      });
    });
  }

  /** Bind subsequent sign-in to a specific GCIP tenant (agency). */
  setTenant(gcipTenantId: string | null): void {
    this.auth().tenantId = gcipTenantId || null;
  }

  getTenant(): string | null {
    return this.auth().tenantId;
  }

  onAuthStateChanged(callback: (user: AuthUser | null) => void): () => void {
    this._ensureInitialized();
    this._listeners.add(callback);
    // Fire immediately with the best-known current state — but ONLY once
    // Firebase has settled its persisted auth state (_bootstrapped). gh #1073:
    // before settle, _currentUser is a not-yet-restored null; emitting it makes
    // AuthContext publish { loading:false, user:null } on a cold document load
    // of a protected route, which strands the dashboard on its unauthenticated
    // dead-end for SELF_HEAL_RELOAD_MS (the reported ~10s hang). A subscriber
    // added before settle is instead notified by the first onIdTokenChanged
    // emission (post-restore), so it never misses the initial state.
    if (typeof window !== 'undefined' && this._bootstrapped) {
      callback(this._currentUser);
    }
    return () => {
      this._listeners.delete(callback);
    };
  }

  /**
   * Email/password sign-in against the currently-set GCIP tenant.
   *
   * Throws GcipAuthError(MFA_CHALLENGE_REQUIRED) when the account has a TOTP
   * factor enrolled (login UI must collect a code → resolveTotpChallenge), or
   * GcipAuthError(MFA_ENROLLMENT_REQUIRED) when sign-in succeeded single-factor
   * and the account must enroll TOTP first (→ startTotpEnrollment).
   */
  async signIn(email: string, password: string): Promise<AuthUser> {
    this._ensureInitialized();
    const { signInWithEmailAndPassword, getMultiFactorResolver } = await import('firebase/auth');
    try {
      const cred = await signInWithEmailAndPassword(this.auth(), email, password);
      // Reached only when the account has no enrolled second factor. MFA is
      // mandatory, so drive the user into enrollment.
      const { multiFactor } = await import('firebase/auth');
      if (this._requireMfa && multiFactor(cred.user).enrolledFactors.length === 0) {
        throw new GcipAuthError(MFA_ENROLLMENT_REQUIRED, 'Multi-factor enrollment required');
      }
      this._currentUser = toAuthUser(cred.user);
      return this._currentUser;
    } catch (err: unknown) {
      if (err instanceof GcipAuthError) throw err;
      const code = (err as { code?: string })?.code;
      if (code === 'auth/multi-factor-auth-required') {
        this._mfaResolver = getMultiFactorResolver(this.auth(), err as never);
        throw new GcipAuthError(MFA_CHALLENGE_REQUIRED, 'Multi-factor challenge required');
      }
      throw err;
    }
  }

  /** Hint uid for the enrolled TOTP factor (for assertionForSignIn). */
  private _totpFactorUid(): string | null {
    const hint = this._mfaResolver?.hints.find((h) => h.factorId === 'totp');
    return hint?.uid ?? this._mfaResolver?.hints[0]?.uid ?? null;
  }

  /**
   * Begin TOTP enrollment for the just-signed-in (single-factor) user.
   * Returns the shared secret + an otpauth:// URL for the authenticator QR.
   */
  async startTotpEnrollment(): Promise<{ secretKey: string; qrCodeUrl: string }> {
    const { multiFactor, TotpMultiFactorGenerator } = await import('firebase/auth');
    const user = this.auth().currentUser;
    if (!user) throw new GcipAuthError(MFA_NO_USER, 'No signed-in user to enroll');
    const session: MultiFactorSession = await multiFactor(user).getSession();
    const secret = await TotpMultiFactorGenerator.generateSecret(session);
    this._enrollSecret = secret;
    const accountName = user.email || 'user';
    return {
      secretKey: secret.secretKey,
      qrCodeUrl: secret.generateQrCodeUrl(accountName, 'Foltrigg'),
    };
  }

  /**
   * Finalize TOTP enrollment with a code from the authenticator. The
   * mfaEnrollment:finalize response already upgrades the session's ID token to
   * carry firebase.sign_in_second_factor=totp (verified empirically; the claim
   * also survives a securetoken refresh), so the user is fully MFA-authenticated
   * here — no re-sign-in or second challenge is needed. Returns the authenticated
   * user; the caller establishes the __session cookie via finishAuthenticated.
   */
  async completeTotpEnrollment(code: string, displayName = 'Authenticator app'): Promise<AuthUser> {
    const { multiFactor, TotpMultiFactorGenerator } = await import('firebase/auth');
    const user = this.auth().currentUser;
    if (!user || !this._enrollSecret) {
      throw new GcipAuthError(MFA_NO_ENROLLMENT, 'No enrollment in progress');
    }
    const assertion = TotpMultiFactorGenerator.assertionForEnrollment(this._enrollSecret, code);
    await multiFactor(user).enroll(assertion, displayName);
    this._enrollSecret = null;
    // Force-refresh so the cached ID token reflects the now-enrolled second factor.
    await user.getIdToken(true);
    this._currentUser = toAuthUser(user);
    return this._currentUser;
  }

  /** Resolve the TOTP challenge with a code; returns the authenticated user. */
  async resolveTotpChallenge(code: string): Promise<AuthUser> {
    const { TotpMultiFactorGenerator } = await import('firebase/auth');
    if (!this._mfaResolver) {
      throw new GcipAuthError(MFA_NO_CHALLENGE, 'No multi-factor challenge in progress');
    }
    const uid = this._totpFactorUid();
    if (!uid) throw new GcipAuthError('NO_TOTP_FACTOR', 'No TOTP factor enrolled');
    const assertion = TotpMultiFactorGenerator.assertionForSignIn(uid, code);
    const cred = await this._mfaResolver.resolveSignIn(assertion);
    this._mfaResolver = null;
    this._currentUser = toAuthUser(cred.user);
    return this._currentUser;
  }

  async signOut(): Promise<void> {
    // CJIS AC-12 app-local logout: blocklist the current bearer at the backend,
    // clear the __session cookie, sign out of Firebase, land on /logout.
    setLoggingOut(true);
    try {
      const user = this.auth().currentUser;
      const bearer = user ? await user.getIdToken() : null;
      const apiUrl = process.env.NEXT_PUBLIC_API_URL || '';
      if (bearer && apiUrl) {
        fetch(`${apiUrl}/api/auth/logout`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${bearer}` },
          keepalive: true,
        }).catch((e) => logger.warn('[GCIPAuthClient] Background blocklist revoke failed', e));
      }
    } catch (e) {
      logger.warn('[GCIPAuthClient] Blocklist revoke failed; continuing local logout', e);
    }

    await fetch('/api/auth/session', { method: 'DELETE' });
    const { signOut } = await import('firebase/auth');
    await signOut(this.auth());
    this._currentUser = null;
    this._notify(null);
    if (typeof window !== 'undefined') {
      // Full page load on purpose: sign-out must drop every piece of client
      // state (router cache, in-memory user, prefetched data), which the
      // client-side router would keep.
      // eslint-disable-next-line @next/next/no-location-assign-relative-destination -- hard reload is the point of a sign-out
      window.location.href = '/logout';
    }
  }

  async lockSession(): Promise<void> {
    // CJIS AC-11 / SC-10 idle lock. Server-authoritative: blocklist the bearer
    // so other tabs stop working and a refresh cannot reuse it, clear the
    // cookie, sign out of Firebase (resume requires a fresh interactive
    // sign-in + MFA). No navigation — caller keeps the lock overlay up.
    //
    // gh #877: the idle lock fires only when EVERY tab of the origin is idle —
    // `useIdleTimeout` broadcasts activity over `BroadcastChannel('session-activity')`
    // so an active foreground tab keeps sibling tabs' timers alive. At that true
    // all-tab-idle point the origin-wide `Clear-Site-Data` full purge (default of
    // `DELETE /api/auth/session`) is the correct, compliant AC-12 teardown and no
    // longer wipes an actively-used tab, so we keep the full purge here.
    setLoggingOut(true);
    try {
      const user = this.auth().currentUser;
      const bearer = user ? await user.getIdToken() : null;
      const apiUrl = process.env.NEXT_PUBLIC_API_URL || '';
      if (bearer && apiUrl) {
        await fetch(`${apiUrl}/api/auth/logout`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${bearer}` },
          keepalive: true,
        });
      }
    } catch (e) {
      logger.warn('[GCIPAuthClient] Blocklist revoke failed during lockSession; continuing', e);
    }

    await fetch('/api/auth/session', { method: 'DELETE' });
    try {
      const { signOut } = await import('firebase/auth');
      await signOut(this.auth());
    } catch (e) {
      logger.warn('[GCIPAuthClient] Firebase signOut failed during lockSession', e);
    }
    this._currentUser = null;

    // gh #1007: stamp localStorage BEFORE the BroadcastChannel post. A frozen
    // background tab misses the channel message but re-reads this stamp on
    // visibilitychange (see app/providers.tsx), so its lock is not lost.
    if (typeof window !== 'undefined') {
      try {
        window.localStorage.setItem(SESSION_LOCK_STORAGE_KEY, String(Date.now()));
      } catch {
        // localStorage unavailable (private mode / quota); channel still fires.
      }
    }

    if (typeof window !== 'undefined' && 'BroadcastChannel' in window) {
      try {
        const bc = new BroadcastChannel(SESSION_LOCK_BROADCAST);
        bc.postMessage({ type: 'lock', ts: Date.now() });
        bc.close();
      } catch {
        // BroadcastChannel unavailable; skip.
      }
    }
  }

  /**
   * Firebase persists auth state in IndexedDB (browserLocalPersistence),
   * shared across tabs, so the new-tab case the OIDC client had to silent-renew
   * for resolves natively. Return the current user once the token is
   * second-factor-attested and within the cap.
   *
   * gh #1073: on a cold document load the IndexedDB restore round-trip is still
   * in flight when AuthContext first calls this, so a synchronous
   * `this.auth().currentUser` read returns null and the dashboard falls through
   * to its unauthenticated dead-end. `authStateReady()` resolves once Firebase
   * has determined the initial auth state (Firebase JS SDK >= 10.8; we ship
   * v12), so awaiting it makes this an actual wait instead of a one-shot read.
   */
  async recoverSession(): Promise<AuthUser | null> {
    this._ensureInitialized();
    try {
      await this.auth().authStateReady();
    } catch {
      // authStateReady() should never reject; fall through to a best-effort
      // read rather than failing recovery outright.
    }
    const user = this.auth().currentUser;
    if (!user) return this._currentUser;
    try {
      const token = await user.getIdToken();
      if (this._requireMfa && !tokenHasSecondFactor(token)) return null;
      if (isSessionCapExceeded(token)) return null;
      this._currentUser = toAuthUser(user);
      return this._currentUser;
    } catch {
      return null;
    }
  }

  async clearLocalState(): Promise<void> {
    try {
      const { signOut } = await import('firebase/auth');
      await signOut(this.auth());
    } catch (e) {
      logger.warn('[GCIPAuthClient] clearLocalState signOut failed', e);
    }
    this._currentUser = null;
    this._notify(null);
  }

  async getIdToken(forceRefresh = false): Promise<string | null> {
    const user = this.auth().currentUser;
    if (!user) return null;
    let token: string;
    try {
      token = await user.getIdToken(forceRefresh);
    } catch {
      return null;
    }
    // CJIS IA-11: never silently extend past the absolute cap.
    if (isSessionCapExceeded(token)) return null;
    // MFA mandatory: a single-factor token is not an app-usable bearer.
    if (this._requireMfa && !tokenHasSecondFactor(token)) return null;
    return token;
  }

  getCurrentUser(): AuthUser | null {
    return this._currentUser;
  }

  async reauthenticate(password: string): Promise<boolean> {
    try {
      const { EmailAuthProvider, reauthenticateWithCredential } = await import('firebase/auth');
      const user = this.auth().currentUser;
      if (!user || !user.email) return false;
      const cred = EmailAuthProvider.credential(user.email, password);
      await reauthenticateWithCredential(user, cred);
      return true;
    } catch (e) {
      logger.warn('[GCIPAuthClient] reauthenticate failed', e);
      return false;
    }
  }

  async updatePassword(currentPassword: string, newPassword: string): Promise<boolean> {
    const ok = await this.reauthenticate(currentPassword);
    if (!ok) return false;
    const { updatePassword } = await import('firebase/auth');
    const user = this.auth().currentUser;
    if (!user) return false;
    try {
      await updatePassword(user, newPassword);
    } catch (e) {
      // Rethrow so callers can surface the real cause (e.g. the server-side
      // GCIP policy's auth/password-does-not-meet-requirements).
      logger.warn('[GCIPAuthClient] updatePassword failed', e);
      throw e;
    }
    return true;
  }

  async sendPasswordResetEmail(email: string, gcipTenantId?: string | null): Promise<boolean> {
    // Sent by GCIP's own mailer (From = custom domain via dnsInfo.customDomain).
    // The reset link is composed by GCIP and lands on callbackUri, the
    // in-boundary app/auth/action page (gcip_email_action_callback_uri, set
    // 2026-09-28), which redirects to /reset-password. ActionCodeSettings.url is
    // only the post-reset continueUrl, NOT a handler relocation.
    // Multi-tenant: bind auth to the GCIP tenant the forgot-password page
    // resolved (null when unknown → base pool). Residual enumeration exposure is
    // the already-accepted client-side oracle of PE-22; the UI stays uniform.
    try {
      const { sendPasswordResetEmail: fbSendPasswordResetEmail } = await import('firebase/auth');
      const auth = this.auth();
      auth.tenantId = gcipTenantId ?? null;
      const actionCodeSettings =
        typeof window !== 'undefined' ? { url: `${window.location.origin}/login` } : undefined;
      await fbSendPasswordResetEmail(auth, email, actionCodeSettings);
      return true;
    } catch (e) {
      logger.warn('[GCIPAuthClient] sendPasswordResetEmail failed', e);
      return false;
    }
  }

  async sendEmailVerification(): Promise<boolean> {
    // Sent by GCIP's own mailer (From = custom domain). Acts on the current
    // user; the verifyEmail link lands on callbackUri (app/auth/action), same as
    // sendPasswordResetEmail.
    try {
      const { sendEmailVerification: fbSendEmailVerification } = await import('firebase/auth');
      const user = this.auth().currentUser;
      if (!user) return false;
      const actionCodeSettings =
        typeof window !== 'undefined' ? { url: `${window.location.origin}/login` } : undefined;
      await fbSendEmailVerification(user, actionCodeSettings);
      return true;
    } catch (e) {
      logger.warn('[GCIPAuthClient] sendEmailVerification failed', e);
      return false;
    }
  }
}
